import { useAppSelector } from "@hooks/store"
import { useNewEditTemplateCollectionContext } from "@pages/collectionAdministration/pages/NewEditTemplateCollection/context/NewEditTemplateCollectionContext"
import { useNewEditTemplateCollectionServices } from "@pages/collectionAdministration/pages/NewEditTemplateCollection/hooks/useNewEditTemplateCollectionServices"
import { getAvailableDebtorsForCharges } from "@pages/collectionAdministration/pages/NewEditTemplateCollection/services/GET/getAvailableDebtorsForCharges"
import { getChargeDebtorTemplate } from "@pages/collectionAdministration/pages/NewEditTemplateCollection/services/GET/getChargeDebtorTemplate"
import { importChargeDebtorTemplate } from "@pages/collectionAdministration/pages/NewEditTemplateCollection/services/POST/importChargeDebtorTemplate"
import verifyResponse from "@thelpers/verifyResponse"
import useCurrentLocation from "@thooks/useCurrentLocation"
import { notification } from "antd"
import { useCallback, useEffect, useState } from "react"

export const useDownloadTemplateCollection = () => {
    const { idToken, currentRol } = useAppSelector(state => state.auth)
    const { param } = useCurrentLocation()
    const { getChargeDebtorsService } = useNewEditTemplateCollectionServices()
    const { setChargeDetail, setChargeDebtorsData } = useNewEditTemplateCollectionContext()
    const [isLoading, setIsLoading] = useState(false)
    const [isOpenModal, setIsOpenModal] = useState(false)
    const [debtors, setDebtors] = useState([])

    const getAvailableDebtorsForChargesService = useCallback(async ({ setLoading }) => {
        setLoading?.(true)
        try {
            const res = await getAvailableDebtorsForCharges({ token: idToken, company_id: currentRol })
            const data = res.data.data.flatMap((item) => item.children)
                .filter((item) => item).map((item, index) => ({ ...item, key: index }))
            return data

        } catch (error) {
            setLoading?.(false)
            console.error(error)
            return []
        }
        finally {
            setIsLoading?.(false)
        }
    }, [idToken, currentRol])

    const importChargeDebtorTemplateService = useCallback(async ({ setLoading, file }) => {
        setLoading?.(true)
        try {
            const formData = new FormData()
            formData.append('charge_id', param.templateId)
            formData.append('file', file)
            const res = await importChargeDebtorTemplate({ token: idToken, formData })
            const status = verifyResponse({ dataResponse: res.data, statusResponse: res.data.status ?? res.status, showErrorToast: true })
            if (!status) throw new Error('Error al importar la plantilla')
            return res
        } catch (error) {
            setLoading?.(false)
            throw new Error(error)
        }
    }, [idToken, param.templateId])

    const getChargeDebtorTemplateService = useCallback(async ({ setLoading, selectedPlaces }) => {
        setLoading?.(true)
        try {
            //Se obtiene el blob del archivo
            const blobFile = await getChargeDebtorTemplate({ token: idToken, places: selectedPlaces })
            const downloadUrl = window.URL.createObjectURL(blobFile)

            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = 'Plantilla de cobros.xlsx'
            link.click()

            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl)
            }, 1000)

            return blobFile
        } catch (error) {
            setLoading?.(false)
            console.error(error)
            return []
        }
    }, [idToken])

    const handleOpenCloseModal = ({ isOpenModal }) => {
        setIsOpenModal(isOpenModal)
    }

    const handleUploadTemplate = async ({ event }) => {
        const file = event.target.files[0]
        //Verficar que el archivo es de tipo xlsx o xls
        if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && file.type !== 'application/vnd.ms-excel') {
            return notification.error({
                message: 'Formato de archivo no soportado',
                description: 'El archivo debe ser de tipo xlsx o xls'
            })
        }
        await importChargeDebtorTemplateService({ setLoading: setIsLoading, file })
        //Obtener los datos del cobro
        const data = await getChargeDebtorsService({ setLoading: setIsLoading, charge_id: param.templateId })
        if (!data) return
        setChargeDebtorsData(data)
        notification.info({
            message: 'Plantilla de cobros importada',
            description: 'Se ha importado la plantilla de cobros con éxito'
        })
    }

    const handleDownloadAllDebtorsTemplate = async () => {
        await getChargeDebtorTemplateService({ setLoading: setIsLoading, selectedPlaces: debtors.map((item) => item.place_id) })
        setIsLoading?.(false)
    }

    useEffect(() => {
        (async () => {
            const data = await getAvailableDebtorsForChargesService({ setLoading: setIsLoading })
            if (!data) return
            setDebtors(data)
        })()
    }, [getAvailableDebtorsForChargesService])

    return {
        isLoading,
        isOpenModal,
        debtors,
        handleDownloadAllDebtorsTemplate,
        handleUploadTemplate,
        handleOpenCloseModal
    }
}